import { Configuration, EventMessage, InteractionRequiredAuthError, PublicClientApplication } from "@azure/msal-browser";
import { AppTheme } from "./utils/types";
import { logger } from "./utils/logger";

// Add the global declaration at the top level of the file
declare global {
  interface Window {
    testTokenRefresh: () => Promise<{success: boolean, message: string}>;
  }
}

interface MsalResult {
	instance: PublicClientApplication | any;
	apiURL: any;
  theme: AppTheme;
  apim:any;
}
  
let msalResult: MsalResult = {
	instance: null,
	apiURL: {},
  theme: {},
  apim: {}
};

/**
 * SECURITY FIX: Removed hardcoded API subscription keys
 * 
 * API keys should be:
 * 1. Stored in environment variables (backend only)
 * 2. Passed through a backend proxy
 * 3. Never exposed in client-side code
 * 
 * Current implementation fetches config from a backend endpoint that handles the API key server-side.
 * The subscription key is now managed through Azure API Management policies.
 */
/**
 * Get API URL for portal configuration
 * 
 * IMPORTANT: Environment variable handling in React (Create React App):
 * - REACT_APP_* variables are replaced at BUILD TIME, not runtime
 * - Values are embedded in the JavaScript bundle and visible to anyone
 * 
 * PRODUCTION SETUP (Recommended):
 * - Do NOT set REACT_APP_APIM_SUBSCRIPTION_KEY_PROD in production builds
 * - Configure Azure API Management inbound policy to add subscription key:
 *   <inbound>
 *     <set-header name="Ocp-Apim-Subscription-Key" exists-action="override">
 *       <value>{{subscription-key-from-named-value}}</value>
 *     </set-header>
 *     <cors allow-credentials="true">
 *       <allowed-origins><origin>https://portal.mwcloud.no</origin></allowed-origins>
 *     </cors>
 *   </inbound>
 * 
 * LOCAL DEVELOPMENT:
 * - Set REACT_APP_APIM_SUBSCRIPTION_KEY_TEST in .env file
 * - This is acceptable for local dev since the key isn't deployed
 */
export const getApiUrl = (url: string): string => {
  const isProduction = url.includes('portal.mwcloud.no');
  const baseUrl = isProduction 
    ? "https://api.mwcloud.no/portal/config"
    : "https://api-t.mwcloud.no/portal/config";
  
  // For local development only - in production, APIM handles the subscription key
  // Note: REACT_APP_* vars are embedded at BUILD TIME (not runtime)
  const subscriptionKey = isProduction
    ? process.env.REACT_APP_APIM_SUBSCRIPTION_KEY_PROD  // Should be undefined in prod builds
    : process.env.REACT_APP_APIM_SUBSCRIPTION_KEY_TEST; // Only for local dev
  
  if (subscriptionKey) {
    return `${baseUrl}?subscription-key=${subscriptionKey}`;
  }
  
  return baseUrl;
}

export const getApiConfig = async (url: string) => {
  const apiUrl = getApiUrl(url);
  try {
    const response = await fetch(apiUrl);
    
    if (!response.ok) {
      throw new Error(`API config fetch failed: ${response.status}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    logger.error('Error fetching API config:', error);
    return {};
  }
};

export const initializeMsal = async () => {
  try {
    const currentUrl = window.location.href;
    let apiConfig = await getApiConfig(currentUrl);
    
    // SECURITY: Validate that we have required auth config
    if (!apiConfig?.auth?.clientId) {
      throw new Error('MSAL configuration is missing required auth parameters (clientId)');
    }
    
    const msalConfig: Configuration = {
      auth: {
        ...apiConfig?.auth,
        tokenRenewalOffsetSeconds: 1800, // 30 minutes in seconds
      },
      system: {
        iframeHashTimeout: 10000,
        navigateFrameWait: 0,
        windowHashTimeout: 9000,
      },
      cache: {
        // SECURITY FIX: Changed from localStorage to sessionStorage
        // localStorage persists across sessions and is more vulnerable to XSS
        // sessionStorage is cleared when the browser tab closes
        cacheLocation: "sessionStorage",
        storeAuthStateInCookie: false
      }
    };

    msalResult.instance = new PublicClientApplication(msalConfig);
    msalResult.theme = apiConfig.theme;
    msalResult.apim = apiConfig; // Store the entire API config

    
    await msalResult.instance.initialize();

    
    msalResult.instance.addEventCallback((event: EventMessage) => {
      // Event handling without logging
    });

    return apiConfig;
  } catch (error) {
    logger.error('Error during MSAL initialization:', error);
    return null;
  }
};

export { msalResult };

export const loginRequest = {
    scopes: ["User.Read"]
};

export const tokenRequest = {
    scopes: ["User.Read"],
};

export const graphConfig = {
    graphMeEndpoint: "https://graph.microsoft.com/v1.0/me"
};

export const refreshTokenWithRedirect = async () => {
  if (!msalResult.instance) {
    return;
  }

  const accounts = msalResult.instance.getAllAccounts();
  if (accounts.length === 0) {
    return;
  }

  const activeAccount = accounts[0];
  
  // SECURITY: Store only path to prevent open redirect
  sessionStorage.setItem('redirectAfterLogin', window.location.pathname + window.location.search);

  try {
    await msalResult.instance.acquireTokenRedirect({
      ...tokenRequest,
      account: activeAccount,
      redirectStartPage: window.location.href
    });
  } catch (error) {
    // Silent error handling
  }
};

export const refreshTokenSilently = async (): Promise<boolean> => {
  try {
    if (msalResult.instance) {
      const accounts = msalResult.instance.getAllAccounts();
      
      const activeAccount = accounts[0];
      if (activeAccount) {
        const isChrome = navigator.userAgent.indexOf("Chrome") !== -1;
        
        if (isChrome) {
          return false;
        }
        
        try {
          await msalResult.instance.acquireTokenSilent({
            ...tokenRequest,
            account: activeAccount
          });
          return true;
        } catch (error) {
          return false;
        }
      }
    }
  } catch (error) {
    // Silent error handling
  }
  return false;
};

let tokenRefreshIntervalId: NodeJS.Timeout | null = null;

const getTokenExpiryMinutes = (account: any): number => {
  if (!account || !account.idTokenClaims || !account.idTokenClaims.exp) {
    return 0;
  }
  
  const expirationTime = account.idTokenClaims.exp;
  const currentTime = Math.floor(Date.now() / 1000);
  const timeUntilExpiration = expirationTime - currentTime;
  
  return Math.floor(timeUntilExpiration / 60);
};

export const setupTokenRefresh = () => {
  if (tokenRefreshIntervalId) {
    return;
  }

  const CHECK_INTERVAL = 60 * 1000; // 1 minute
  
  const checkAndRefreshToken = async () => {
    if (!msalResult.instance) {
      return;
    }
    
    try {
      const accounts = msalResult.instance.getAllAccounts();
      if (accounts.length === 0) {
        return;
      }
      
      const account = accounts[0];
      const minutesRemaining = getTokenExpiryMinutes(account);
      
      if (minutesRemaining < 5) {
        try {
          await msalResult.instance.acquireTokenSilent({
            ...tokenRequest,
            account: account,
            forceRefresh: true
          });
        } catch (error) {
          if (error instanceof InteractionRequiredAuthError) {
            try {
              await msalResult.instance.acquireTokenSilent({
                ...tokenRequest,
                account: account
              });
            } catch (fallbackError) {
              logger.error('Token refresh failed after multiple attempts');
            }
          }
        }
      }
    } catch (error) {
      logger.error('Error during token check:', error);
    }
  };

  tokenRefreshIntervalId = setInterval(checkAndRefreshToken, CHECK_INTERVAL);
  setTimeout(checkAndRefreshToken, 1000);
};

export const getIdToken = async () => {  
  try {
    let msalInstanc: any = msalResult.instance;
    if (!msalInstanc) {
      return null;
    }
    
    let activeAccount: any = null;
    try {
      const accounts = msalInstanc.getAllAccounts();
      activeAccount = accounts[0];
    } catch (accountError) {
      return null;
    }
    
    if (!activeAccount) {
      msalInstanc.loginRedirect(loginRequest);
      return null;
    }

    const minutesRemaining = getTokenExpiryMinutes(activeAccount);
    
    try {
      const response = await msalInstanc.acquireTokenSilent({
        ...tokenRequest,
        account: activeAccount
      });
      return response.idToken;
    } catch (error) {
      if (minutesRemaining > 1) {
        return activeAccount.idToken || activeAccount.idTokenClaims?.rawIdToken;
      }
      
      try {
        // SECURITY: Store only path to prevent open redirect
        sessionStorage.setItem('redirectAfterLogin', window.location.pathname + window.location.search);
        await msalInstanc.acquireTokenRedirect({
          ...loginRequest,
          account: activeAccount
        });
        return null;
      } catch (redirectError) {
        msalInstanc.logoutRedirect();
        return null;
      }
    }
  } catch (unhandledError) {
    return null;
  }
};

export const testTokenRefresh = async (): Promise<{success: boolean, message: string}> => {
  try {
    if (!msalResult.instance) {
      return { success: false, message: 'MSAL instance not initialized' };
    }

    const accounts = msalResult.instance.getAllAccounts();
    if (accounts.length === 0) {
      return { success: false, message: 'No active accounts found' };
    }

    const activeAccount = accounts[0];
    const originalExpiryMinutes = getTokenExpiryMinutes(activeAccount);

    try {
      const response = await msalResult.instance.acquireTokenSilent({
        ...tokenRequest,
        account: activeAccount,
        forceRefresh: true
      });

      const newExpiryMinutes = getTokenExpiryMinutes(response.account);
      
      return {
        success: true,
        message: `Token refreshed successfully. Original expiry: ${originalExpiryMinutes}min, New expiry: ${newExpiryMinutes}min`
      };
    } catch (error: any) {
      if (error instanceof InteractionRequiredAuthError) {
        return { 
          success: false, 
          message: 'Interaction required - token cannot be refreshed silently' 
        };
      }
      return { 
        success: false, 
        message: `Error refreshing token: ${error?.message || 'Unknown error'}` 
      };
    }
  } catch (error: any) {
    return { 
      success: false, 
      message: `Unexpected error: ${error?.message || 'Unknown error'}` 
    };
  }
};

// Only expose in non-production environments
if (process.env.NODE_ENV !== 'production' && typeof window !== 'undefined') {
  window.testTokenRefresh = testTokenRefresh;
}  